import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SETTINGS_KEYS } from '../../config/environment'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { savedViewService } from '../../services/saved-view.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { useAppStore } from '../../store/useAppStore'
import { SavedView } from '../../types'
// import { useSetting } from '../../hooks/useSettings';
// import { useAuth } from '../../hooks/useAuth';
import { DashboardLayoutOptions, loadDashboardLayoutOptions, saveDashboardLayoutOptions, WidgetType } from '../../utils/dashboardLayout'
import { DocumentationDialog } from '../common/DocumentationDialog'
import PageHeader from '../common/PageHeader'
import { DocumentUpload } from '../Upload/DocumentUpload'
import { ConsumerStatusWidget } from './ConsumerStatusWidget'
import './Dashboard.scss'
import { DashboardLayoutDialog } from './DashboardLayoutDialog'
import { SavedViewWidget } from './SavedViewWidget'
import { SortableWidgetWrapper } from './SortableWidgetWrapper'
import { StatisticsWidget } from './StatisticsWidget'

export const Dashboard: React.FC = () => {
  const { t } = useTranslation()
  const { setSavedViews } = useAppStore()
  const canViewSavedViews = permissionsService.currentUserCan(PermissionAction.View, PermissionType.SavedView)
  const canAddDocument = permissionsService.currentUserCan(PermissionAction.Add, PermissionType.Document)
  // const { user } = useAuth();
  const [dashboardViews, setDashboardViews] = useState<SavedView[]>([])
  const [loading, setLoading] = useState(true)
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [showLayoutDialog, setShowLayoutDialog] = useState(false)
  const [layoutOptions, setLayoutOptions] = useState<DashboardLayoutOptions>(() => loadDashboardLayoutOptions())
  // const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // const displayName = user?.first_name || user?.username || '';
  // const subtitle = displayName
  //   ? t('hello_welcome_to', { name: displayName, app: appTitle }) || `Hello ${displayName}, welcome to ${appTitle}`
  //   : t('welcome_to', { app: appTitle }) || `Welcome to ${appTitle}`;

  const loadDashboardViews = useCallback(async () => {
    if (!canViewSavedViews) {
      setDashboardViews([])
      setSavedViews([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const response = await savedViewService.getSavedViews({ page_size: 100 })
      const dashboardViewsUnsorted = response.results.filter((view) => view.show_on_dashboard)

      const sortOrder: number[] = settingsService.get(SETTINGS_KEYS.DASHBOARD_VIEWS_SORT_ORDER) || []

      const sortedDashboardViews =
        sortOrder.length > 0
          ? (sortOrder
              .map((id) => dashboardViewsUnsorted.find((v) => v.id === id))
              .concat(dashboardViewsUnsorted.filter((v) => !sortOrder.includes(v.id)))
              .filter((v) => v) as SavedView[])
          : dashboardViewsUnsorted

      setDashboardViews(sortedDashboardViews)
      setSavedViews(response.results)
    } catch (_error) {
    } finally {
      setLoading(false)
    }
  }, [setSavedViews, canViewSavedViews])

  useEffect(() => {
    loadDashboardViews()
  }, [loadDashboardViews])

  const handleWidgetDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const activeId = active.id as WidgetType
      const overId = over.id as WidgetType

      if (['consumer-status', 'statistics', 'saved-views'].includes(activeId) && ['consumer-status', 'statistics', 'saved-views'].includes(overId)) {
        const currentOrder = [...layoutOptions.widgetOrder]
        const oldIndex = currentOrder.indexOf(activeId)
        const newIndex = currentOrder.indexOf(overId)

        const reorderedWidgets = arrayMove(currentOrder, oldIndex, newIndex)
        const newOptions = { ...layoutOptions, widgetOrder: reorderedWidgets }

        setLayoutOptions(newOptions)
        saveDashboardLayoutOptions(newOptions)
        toastService.showSuccess(t('dashboard_updated') || 'Dashboard updated')
      }
    }
  }

  const handleSavedViewsDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setDashboardViews((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)

        const reorderedItems = arrayMove(items, oldIndex, newIndex)

        settingsService
          .updateDashboardViewsSort(reorderedItems.map((v) => v.id))
          .then(() => {
            toastService.showSuccess(t('dashboard_updated') || 'Dashboard updated')
          })
          .catch((_error) => {
            toastService.showError(t('error_updating_dashboard') || 'Error updating dashboard')
          })

        return reorderedItems
      })
    }
  }

  const handleApplyLayout = (options: DashboardLayoutOptions) => {
    setLayoutOptions(options)
    saveDashboardLayoutOptions(options)
    toastService.showSuccess(t('dashboard_organized') || 'Dashboard organized')
  }

  const renderStatisticsAndUpload = () => (
    <div className="row g-4">
      <div className={canAddDocument ? 'col-12 col-lg-8 col-xl-9' : 'col-12'}>
        <div className="dashboard-widget-wrapper">
          <StatisticsWidget />
        </div>
      </div>
      {canAddDocument && (
        <div className="col-12 col-lg-4 col-xl-3">
          <div className="dashboard-widget-wrapper">
            <DocumentUpload />
          </div>
        </div>
      )}
    </div>
  )

  const renderWidget = (widgetType: WidgetType) => {
    switch (widgetType) {
      case 'consumer-status':
        return (
          <SortableWidgetWrapper id="consumer-status">
            <ConsumerStatusWidget />
          </SortableWidgetWrapper>
        )
      case 'statistics':
        return <SortableWidgetWrapper id="statistics">{renderStatisticsAndUpload()}</SortableWidgetWrapper>
      case 'saved-views':
        if (!canViewSavedViews) return null
        return <SortableWidgetWrapper id="saved-views">{renderSavedViews()}</SortableWidgetWrapper>
      default:
        return null
    }
  }

  const renderSavedViews = () => {
    if (loading) {
      return (
        <div className="dashboard-card dashboard-card--loading">
          <div className="dashboard-card__body">
            <div className="text-center py-5">
              <div className="dashboard-spinner">
                <div className="dashboard-spinner__circle"></div>
                <div className="dashboard-spinner__circle"></div>
                <div className="dashboard-spinner__circle"></div>
              </div>
              <p className="mt-4 text-muted">{t('loading_views')}</p>
            </div>
          </div>
        </div>
      )
    }

    if (dashboardViews.length === 0) {
      return (
        <div className="dashboard-card dashboard-card--empty">
          <div className="dashboard-card__body">
            <div className="text-center py-4">
              <div className="dashboard-empty-icon mb-3">
                <i className="bi bi-folder-plus"></i>
              </div>
              <p className="mb-2 fw-semibold">Kreirajte svoj prvi prikaz</p>
              <p className="text-muted small mb-0">
                Sačuvani prikazi se mogu kreirati iz{' '}
                <Link to="/documents" className="dashboard-link">
                  liste dokumenata
                </Link>
              </p>
            </div>
          </div>
        </div>
      )
    }

    const viewsPerRow = layoutOptions.viewsPerRow

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSavedViewsDragEnd}>
        <SortableContext items={dashboardViews.map((v) => v.id)} strategy={verticalListSortingStrategy}>
          <div
            className={`dashboard-views dashboard-views--${viewsPerRow}`}
            data-views-per-row={viewsPerRow}
            style={{ '--views-per-row': viewsPerRow } as React.CSSProperties}
          >
            {dashboardViews.map((view) => (
              <div key={view.id} className="dashboard-view-col">
                <div className="dashboard-view-item">
                  <SavedViewWidget savedView={view} />
                </div>
              </div>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    )
  }

  return (
    <div className="dashboard">
      <div className="dashboard-background">
        <div className="dashboard-background__gradient"></div>
        <div className="dashboard-background__pattern"></div>
      </div>

      <PageHeader
        title={t('dashboard')}
        // subtitle={subtitle}
      >
        <Button
          variant="link"
          size="sm"
          onClick={() => setShowLayoutDialog(true)}
          title={t('organize_dashboard')}
          className="p-0 me-2"
          style={{ width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="bi bi-grid"></i>
        </Button>
        <Button
          variant="link"
          size="sm"
          onClick={() => setShowDocsDialog(true)}
          title={t('help')}
          className="p-0"
          style={{ width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="bi bi-info-circle"></i>
        </Button>
      </PageHeader>

      <div className="dashboard-content">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleWidgetDragEnd}>
          <SortableContext items={layoutOptions.widgetOrder} strategy={verticalListSortingStrategy}>
            {layoutOptions.widgetOrder.map((widgetType: WidgetType) => (
              <div key={widgetType}>{renderWidget(widgetType)}</div>
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <DocumentationDialog show={showDocsDialog} onHide={() => setShowDocsDialog(false)} docKey="dashboard" />
      <DashboardLayoutDialog
        show={showLayoutDialog}
        onHide={() => setShowLayoutDialog(false)}
        onApply={handleApplyLayout}
        currentOptions={layoutOptions}
      />
    </div>
  )
}
